import { Outlet, NavLink } from 'react-router-dom'
import { useState } from 'react'
import type { TranslationKey } from '@autoplan/common'
import { useLocale } from '@autoplan/web-sanity'

const SIDEBAR_W = '4.5rem' // matches --spacing-6xl / ap:pl-6xl

function resolveFleetPortalOrigin(): string | null {
  if (typeof window === 'undefined') return null
  const { hostname, protocol, port } = window.location
  if (hostname.includes('sanity-preview')) {
    const fleetHostname = hostname.replace('sanity-preview', 'fleet-portal')
    return `${protocol}//${fleetHostname}${port ? `:${port}` : ''}`
  }
  return null
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20} aria-hidden="true">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  )
}

function CarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20} aria-hidden="true">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
    </svg>
  )
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20} aria-hidden="true">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  )
}

function ReportIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20} aria-hidden="true">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
    </svg>
  )
}

const baseItemStyle: React.CSSProperties = {
  padding: '0.625rem',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.15s',
}

type ExternalNavItemProps = {
  href: string
  title: string
  children: React.ReactNode
}

function ExternalNavItem({ href, title, children }: ExternalNavItemProps) {
  const [hovered, setHovered] = useState(false)
  return (
    <a
      href={href}
      title={title}
      style={{
        ...baseItemStyle,
        textDecoration: 'none',
        color: hovered ? '#111827' : '#6b7280',
        backgroundColor: hovered ? '#f3f4f6' : 'transparent',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  )
}

const externalModules: Array<{ label: TranslationKey; path: string; Icon: () => React.ReactNode }> = [
  { label: 'carPark.nav.title', path: '/carpark', Icon: CarIcon },
  { label: 'employeeList.nav.title', path: '/employee-list', Icon: PeopleIcon },
  { label: 'reports.nav.title', path: '/reports', Icon: ReportIcon },
]

export function PreviewShell() {
  const { t } = useLocale()
  const fleetPortalOrigin = resolveFleetPortalOrigin()

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: SIDEBAR_W,
          height: '100vh',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '0.75rem',
          gap: '4px',
          zIndex: 50,
          boxSizing: 'border-box',
        }}
      >
        <NavLink
          to="/"
          end
          title={t('home.nav.title')}
          style={({ isActive }) => ({
            ...baseItemStyle,
            textDecoration: 'none',
            color: isActive ? '#0048D9' : '#6b7280',
            backgroundColor: isActive ? '#eff6ff' : 'transparent',
          })}
        >
          <HomeIcon />
        </NavLink>

        {externalModules.map(({ label, path, Icon }) =>
          fleetPortalOrigin ? (
            <ExternalNavItem key={label} href={`${fleetPortalOrigin}${path}`} title={t(label)}>
              <Icon />
            </ExternalNavItem>
          ) : (
            <div
              key={label}
              title={`${t(label)} (${t('home.preview.notAvailable')})`}
              style={{ ...baseItemStyle, color: '#d1d5db', cursor: 'default' }}
            >
              <Icon />
            </div>
          ),
        )}
      </nav>
      <div style={{ paddingLeft: SIDEBAR_W, flex: 1, minWidth: 0 }}>
        <Outlet />
      </div>
    </div>
  )
}
